import { FC } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';

import { FUZE_CASH, PAYMENT_STEP } from '~common';
import { LoadingScreen } from '~components/atoms';
import { ListItem } from '~components/atoms/new-step-three/ListItem';
import { LocalizationTypography } from '~components/organisms/localization';
import {
  useAccountBalancesContext,
  usePaymentStepContext,
  useStationPricingContext,
} from '~providers';
import { getCurrencyCountryCode } from '~utils/currency.util';
import { formatCurrency } from '~utils/index';

export const FUZECashBalanceInCurrencies: FC = () => {
  const { handleSetPaymentStep } = usePaymentStepContext();
  const { accountBalancesData, isLoadingAccountBalances } =
    useAccountBalancesContext();
  const { stationPricing } = useStationPricingContext();

  if (isLoadingAccountBalances) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Stack direction="row">
        <IconButton
          aria-label="back"
          size="small"
          color="inherit"
          sx={{ paddingY: 0 }}
          onClick={() => handleSetPaymentStep(PAYMENT_STEP.RENT_A_CHARGER)}
        >
          <ArrowBackIos />
        </IconButton>

        <LocalizationTypography
          textId="FUZE Cash"
          defaultText="FUZE Cash"
          variant="Poppins/Heading-3"
        />
      </Stack>
      <Stack gap={2}>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.text.redeemFuzeCash"
          defaultText="Redeemed promotions are added to your account. Your next rental will use available FUZE Cash first."
          variant="Inter/Body-sm"
          color="text.secondary"
        />
      </Stack>

      {/* CURRENCY LIST */}
      <Stack direction="column" spacing={1}>
        {accountBalancesData?.accountBalances.map((item, index) => {
          const countryCode = getCurrencyCountryCode(item.currency);
          const isOutOfRegion = item.symbol !== stationPricing?.symbol;

          return (
            <ListItem
              hoverable={false}
              key={item.currency}
              enableFullDivider={
                index < accountBalancesData?.accountBalances.length - 1
              }
              enableDivider={false}
              leftContainerStyle={isOutOfRegion ? { opacity: 0.6 } : {}}
              left={
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Typography sx={{ fontSize: '32px' }}>
                    {getUnicodeFlagIcon(countryCode)}
                  </Typography>
                  <Stack
                    direction="column"
                    spacing={0}
                    alignItems="flex-start"
                    sx={{ minWidth: '100%' }}
                  >
                    <Typography variant="Poppins/Heading-6" color="#111928">
                      {`${item.currency}`}
                    </Typography>
                    {isOutOfRegion && (
                      <LocalizationTypography
                        textId="app.rentalPage.newStepThree.text.outOfRegion"
                        defaultText="Out of region"
                        variant="Inter/Body-xs"
                        color="#9CA3AF"
                        sx={{ mt: 0.25 }}
                      />
                    )}
                  </Stack>
                </Stack>
              }
              right={
                <Stack
                  direction="row"
                  justifyContent="right"
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="Poppins/Heading-6"
                    color={isOutOfRegion ? '#6B7280' : '#111928'}
                  >
                    {item.availableBalance >= FUZE_CASH.FUZE_CASH_MAX_AMOUNT
                      ? `${item.symbol}${FUZE_CASH.FUZE_CASH_MAX_AMOUNT_DISPLAY}`
                      : formatCurrency(item.availableBalance, item.symbol, 2)}
                  </Typography>
                </Stack>
              }
            />
          );
        })}
      </Stack>
    </>
  );
};
